import { useState, useRef, useEffect } from 'react';
import { Sparkles, X, Send, Bot, User } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { askAssistant, QuotaExceededError } from '../../services/AiAssistantService';
import { MAX_QUESTION_CHARS, questionTooLongMessage } from '../../utils/aiChatLimits';
import { useUserRole } from '../../context/UserRoleContext';
import { useUserContext } from '../../context/UserContext';
import { useLang } from '../../context/LangContext';
import { useTheme } from '../../context/ThemeContext';
import { colors } from '../../utils/theme';
import { ASK_AI_EVENT } from '../../utils/aiBridge';

interface Message {
  role: 'user' | 'assistant' | 'notice';
  content: string;
  timestamp: Date;
}

/** Discreto — solo cerca del límite, sin contador permanente en el header. */
function quotaLimitReachedText(lang: string): string {
  return lang === 'en'
    ? "You've reached your monthly consultation limit. [Upgrade your plan](/billing) to keep using Killa without interruptions →"
    : 'Alcanzaste tu límite mensual de consultas. [Actualiza tu plan](/billing) para seguir usando a Killa sin interrupciones →';
}

function quotaLowText(lang: string, remaining: number): string {
  return lang === 'en'
    ? `💬 You have ${remaining} consultation${remaining === 1 ? '' : 's'} left this month.`
    : `💬 Te quedan ${remaining} consulta${remaining === 1 ? '' : 's'} este mes.`;
}

const SUGGESTIONS_ES = [
  '¿Cuánto IGV tengo que pagar?',
  '¿Qué puedo deducir como gasto?',
  '¿Cuándo tengo que declarar a SUNAT?',
  '¿Cómo sé si mi negocio está ganando?',
];

const SUGGESTIONS_EN = [
  'How much IGV do I pay?',
  'What can I deduct as an expense?',
  'When do I need to file with SUNAT?',
  'How do I know if my business is profitable?',
];

export default function AiCopilotFab() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const { activeUser } = useUserContext();
  const uid = activeUser.id !== 'local-user' ? activeUser.id : null;
  const { role } = useUserRole();
  const { lang } = useLang();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const c = colors(isDark);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Preguntas enviadas desde otras pantallas (p. ej. Biblioteca): abre el panel y envía por la misma ruta de siempre.
  const sendRef = useRef<(q: string) => void>(() => {});
  useEffect(() => {
    function onAsk(e: Event) {
      const q = (e as CustomEvent<{ question?: string }>).detail?.question?.trim();
      if (!q || q.length > MAX_QUESTION_CHARS) return;
      setOpen(true);
      sendRef.current(q);
    }
    window.addEventListener(ASK_AI_EVENT, onAsk);
    return () => window.removeEventListener(ASK_AI_EVENT, onAsk);
  }, []);

  const suggestions = lang === 'en' ? SUGGESTIONS_EN : SUGGESTIONS_ES;
  const title = 'Killa';

  async function sendMessage(question: string) {
    // Add user message immediately
    const userMsg: Message = { role: 'user', content: question, timestamp: new Date() };
    setMessages((prev) => {
      return [...prev, userMsg];
    });
    setLoading(true);

    if (!uid) {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Error: sesión no disponible. Recarga la página.', timestamp: new Date() },
      ]);
      setLoading(false);
      return;
    }

    try {
      const { text, quota } = await askAssistant(question, role ?? 'emprendedor', lang === 'en' ? 'en' : 'es');
      const safeAnswer = text?.trim() ? text : 'Sin respuesta del asistente.';
      setMessages((prev) => {
        const next: Message[] = [...prev, { role: 'assistant', content: safeAnswer, timestamp: new Date() }];
        if (quota) {
          const remaining = quota.max - quota.used;
          if (remaining <= 0) {
            next.push({ role: 'notice', content: quotaLimitReachedText(lang), timestamp: new Date() });
          } else if (remaining <= 3) {
            next.push({ role: 'notice', content: quotaLowText(lang, remaining), timestamp: new Date() });
          }
        }
        return next;
      });
    } catch (err) {
      if (err instanceof QuotaExceededError) {
        setMessages((prev) => [
          ...prev,
          { role: 'notice', content: quotaLimitReachedText(lang), timestamp: new Date() },
        ]);
      } else {
        const msg = err instanceof Error ? err.message : 'Lo siento, ocurrió un error. Intenta de nuevo.';
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: msg, timestamp: new Date() },
        ]);
      }
    } finally {
      setLoading(false);
    }
  }

  sendRef.current = sendMessage;

  // Tope de la pregunta: se avisa antes de enviar y NO se borra lo escrito (el servidor nunca recorta ni vacía el mensaje).
  const tooLong = input.length > MAX_QUESTION_CHARS;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!input.trim() || loading || tooLong) return;
    const q = input.trim();
    setInput('');
    sendMessage(q);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!input.trim() || loading || tooLong) return;
      const q = input.trim();
      setInput('');
      sendMessage(q);
    }
  }

  function formatTime(date: Date) {
    return date.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' });
  }

  return (
    <>
      {/* FAB button */}
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full
                   text-white shadow-lg hover:opacity-90
                   flex items-center justify-center transition-all
                   hover:scale-105 active:scale-95"
        style={{ background: 'var(--brand)' }}
        aria-label="Killa, asistente contable"
      >
        {open ? <X size={20} /> : <Sparkles size={20} />}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-20 right-6 z-50 flex flex-col shadow-2xl rounded-2xl overflow-hidden"
          style={{
            width: 360,
            maxHeight: 560,
            background: c.modal,
            border: `1px solid ${c.border}`,
          }}
        >
          {/* Panel header */}
          <div
            className="flex items-center justify-between px-4 py-3 shrink-0"
            style={{ borderBottom: `1px solid ${c.borderSoft}` }}
          >
            <div className="flex items-center gap-2">
              <Sparkles size={15} style={{ color: 'var(--brand)' }} />
              <span className="font-semibold text-sm" style={{ color: c.textPrimary }}>{title}</span>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => setOpen(false)} style={{ color: c.textMuted }}>
                <X size={15} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div
            className="flex-1 overflow-y-auto flex flex-col gap-3 p-4"
            style={{ minHeight: 200, maxHeight: 380 }}
          >
            {messages.length === 0 && !loading ? (
              <div className="flex flex-col items-center justify-center h-full gap-3 py-8">
                <div
                  style={{
                    width: 48, height: 48, borderRadius: '50%',
                    background: 'var(--brand-soft)', border: '1px solid var(--brand-soft)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <Bot size={22} style={{ color: 'var(--brand)' }} />
                </div>
                <p className="text-slate-400 text-xs text-center px-2">
                  {lang === 'en'
                    ? "Hi! I'm Killa 🌙 Your accounting assistant — here to help with SUNAT, IGV, PLE and anything about your accounting. What can I help with today?"
                    : '¡Hola! Soy Killa 🌙 Tu asistente contable — aquí para ayudarte con SUNAT, IGV, PLE y todo lo que necesites sobre tu contabilidad. ¿En qué te ayudo hoy?'}
                </p>
                <div className="flex flex-col gap-1.5 w-full mt-1">
                    {suggestions.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => sendMessage(s)}
                        disabled={loading}
                        className="text-left text-xs rounded-lg px-3 py-2 transition-colors text-slate-300"
                        style={{ background: c.modal, border: `1px solid ${c.borderSoft}`, cursor: 'pointer' }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--brand-bd)'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = c.borderSoft; }}
                      >
                        {s}
                      </button>
                    ))}
                </div>
              </div>
            ) : (
              <>
                {messages.map((msg, idx) => (
                  msg.role === 'notice' ? (
                    <div key={idx} className="text-center px-2">
                      <span className="text-[11px] leading-relaxed" style={{ color: c.textMuted }}>
                        <ReactMarkdown
                          components={{
                            p: ({ children }) => <>{children}</>,
                            a: ({ children, href }) => (
                              <a href={href} className="underline font-medium" style={{ color: 'var(--brand)' }}>{children}</a>
                            ),
                          }}
                        >
                          {msg.content}
                        </ReactMarkdown>
                      </span>
                    </div>
                  ) : (
                  <div key={idx} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    {msg.role === 'assistant' && (
                      <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--brand-soft)', border: '1px solid var(--brand-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                        <Bot size={11} style={{ color: 'var(--brand)' }} />
                      </div>
                    )}
                    <div className="flex flex-col gap-0.5" style={{ maxWidth: '80%' }}>
                      <div
                        className="rounded-2xl px-3 py-2 text-xs leading-relaxed"
                        style={
                          msg.role === 'user'
                            ? { background: c.card, border: '1px solid var(--brand-bd)', color: c.textPrimary }
                            : { background: c.modal, border: `1px solid ${c.border}`, color: c.textNormal }
                        }
                      >
                        {msg.role === 'assistant' ? (
                          <ReactMarkdown
                            components={{
                              p: ({ children }) => <p className="text-xs mb-1">{children}</p>,
                              ul: ({ children }) => <ul className="list-disc pl-4 mb-1">{children}</ul>,
                              ol: ({ children }) => <ol className="list-decimal pl-4 mb-1">{children}</ol>,
                              li: ({ children }) => <li className="text-xs ml-3">{children}</li>,
                              strong: ({ children }) => <strong className="font-semibold" style={{ color: c.textPrimary }}>{children}</strong>,
                              h1: ({ children }) => <p className="font-bold text-sm mt-2">{children}</p>,
                              h2: ({ children }) => <p className="font-bold text-sm mt-2">{children}</p>,
                              h3: ({ children }) => <p className="font-semibold text-xs mt-1">{children}</p>,
                              table: ({ children }) => (
                                <div className="overflow-x-auto text-xs">
                                  <table className="min-w-full border-collapse">{children}</table>
                                </div>
                              ),
                              th: ({ children }) => <th className="border border-slate-600 px-2 py-1 text-left text-xs font-medium">{children}</th>,
                              td: ({ children }) => <td className="border border-slate-600 px-2 py-1 text-xs">{children}</td>,
                            }}
                          >
                            {msg.content}
                          </ReactMarkdown>
                        ) : (
                          msg.content
                        )}
                      </div>
                      <span style={{ fontSize: 11, color: c.textMuted, textAlign: msg.role === 'user' ? 'right' : 'left', paddingLeft: msg.role === 'user' ? 0 : 4, paddingRight: msg.role === 'user' ? 4 : 0 }}>
                        {formatTime(msg.timestamp)}
                      </span>
                    </div>
                    {msg.role === 'user' && (
                      <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--brand-soft)', border: '1px solid var(--brand-bd)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                        <User size={11} style={{ color: 'var(--brand)' }} />
                      </div>
                    )}
                  </div>
                  )
                ))}

                {/* 3-dot loading */}
                {loading && (
                  <div className="flex gap-2 justify-start">
                    <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--brand-soft)', border: '1px solid var(--brand-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                      <Bot size={11} style={{ color: 'var(--brand)' }} />
                    </div>
                    <div className="rounded-2xl px-3 py-2 text-xs flex items-center gap-1.5" style={{ background: c.modal, border: `1px solid ${c.border}`, color: 'var(--brand)' }}>
                      <span className="animate-bounce" style={{ animationDelay: '0s' }}>•</span>
                      <span className="animate-bounce" style={{ animationDelay: '0.1s' }}>•</span>
                      <span className="animate-bounce" style={{ animationDelay: '0.2s' }}>•</span>
                    </div>
                  </div>
                )}
              </>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          {tooLong && (
            <p role="alert" className="px-3 pt-2 text-xs" style={{ color: 'var(--negative)' }}>
              {questionTooLongMessage(input.length, lang === 'en' ? 'en' : 'es')}
            </p>
          )}
          <form onSubmit={handleSubmit} className="flex gap-2 p-3 shrink-0" style={{ borderTop: `1px solid ${c.borderSoft}` }}>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={lang === 'en' ? 'Type your question...' : 'Escribe tu pregunta...'}
              disabled={loading}
              className="flex-1 rounded-xl px-3 py-2 text-xs outline-none bg-transparent"
              style={{ background: c.modal, border: `1px solid ${c.border}`, color: c.textPrimary }}
              onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--brand-bd)'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = c.border; }}
            />
            <button
              type="submit"
              disabled={loading || !input.trim() || tooLong}
              className="rounded-xl px-3 py-2 flex items-center justify-center transition-colors"
              style={{
                background: loading || !input.trim() || tooLong ? 'var(--brand-bd)' : 'var(--brand)',
                color: 'var(--canvas)',
                opacity: loading || !input.trim() ? 0.5 : 1,
                cursor: loading || !input.trim() ? 'not-allowed' : 'pointer',
                minWidth: 36,
              }}
            >
              <Send size={13} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
